/**
 * Word Document Service
 *
 * Provides methods to manipulate the Word document content
 * using the Office.js Word API.
 */

/**
 * Check if Word API is available
 */
function isWordAvailable(): boolean {
  return typeof Word !== "undefined" && typeof Word.run === "function";
}

/**
 * Insert text at the current cursor position
 */
export async function insertTextAtCursor(text: string): Promise<void> {
  if (!isWordAvailable()) {
    throw new Error("Word API not available");
  }

  return Word.run(async (context) => {
    const selection = context.document.getSelection();
    selection.insertText(text, Word.InsertLocation.replace);
    await context.sync();
  });
}

/**
 * Insert text at the end of the document
 */
export async function insertTextAtEnd(text: string): Promise<void> {
  if (!isWordAvailable()) {
    throw new Error("Word API not available");
  }

  return Word.run(async (context) => {
    const body = context.document.body;
    body.insertText(text, Word.InsertLocation.end);
    await context.sync();
  });
}

/**
 * Get the currently selected text
 */
export async function getSelectedText(): Promise<string> {
  if (!isWordAvailable()) {
    return "";
  }

  return Word.run(async (context) => {
    const selection = context.document.getSelection();
    selection.load("text");
    await context.sync();
    return selection.text;
  });
}

/**
 * Get the currently selected content as HTML
 * Preserves formatting like bold, italic, headings, etc.
 */
export async function getSelectedHtml(): Promise<string> {
  if (!isWordAvailable()) {
    return "";
  }

  return Word.run(async (context) => {
    const selection = context.document.getSelection();
    const html = selection.getHtml();
    await context.sync();
    return html.value;
  });
}

/**
 * Get the currently selected content as OOXML
 * Most complete format, includes all Word formatting details
 */
export async function getSelectedOoxml(): Promise<string> {
  if (!isWordAvailable()) {
    return "";
  }

  return Word.run(async (context) => {
    const selection = context.document.getSelection();
    const ooxml = selection.getOoxml();
    await context.sync();
    return ooxml.value;
  });
}

/**
 * Replace the current selection with new text
 */
export async function replaceSelection(text: string): Promise<void> {
  if (!isWordAvailable()) {
    throw new Error("Word API not available");
  }

  return Word.run(async (context) => {
    const selection = context.document.getSelection();
    selection.insertText(text, Word.InsertLocation.replace);
    await context.sync();
  });
}

/**
 * Get the entire document text
 */
export async function getDocumentText(): Promise<string> {
  if (!isWordAvailable()) {
    return "";
  }

  return Word.run(async (context) => {
    const body = context.document.body;
    body.load("text");
    await context.sync();
    return body.text;
  });
}

/**
 * Insert HTML content at the cursor
 */
export async function insertHtmlAtCursor(html: string): Promise<void> {
  if (!isWordAvailable()) {
    throw new Error("Word API not available");
  }

  return Word.run(async (context) => {
    const selection = context.document.getSelection();
    selection.insertHtml(html, Word.InsertLocation.replace);
    await context.sync();
  });
}

// ============================================================
// Surgical Correction Functions (Occurrence-based)
// ============================================================

/**
 * Correction item type
 */
export interface CorrectionItem {
  paragraph: number;
  original: string;
  occurrence: number;
  corrected: string;
  bodyParagraphIndex?: number; // Index in document body (for apply after navigation)
}

/**
 * Validation result type
 */
export interface ValidationResult {
  valid: boolean;
  error?: string;
  foundCount?: number;
}

/**
 * Result type for paragraph analysis
 */
export interface ParagraphAnalysisData {
  text: string; // Indexed text for AI: "[0] First para\n[1] Second para..."
  indexMap: number[]; // Maps selection index → body index: [5, 6, 7] means selection[0] = body[5]
  isFullDocument: boolean; // If true, indexMap is identity (selection index = body index)
}

/**
 * Get paragraphs from SELECTION with indices AND body index mapping
 * Returns both the indexed text for AI and the mapping to apply corrections correctly
 */
export async function getParagraphsIndexed(): Promise<ParagraphAnalysisData> {
  if (!isWordAvailable()) {
    return { text: "", indexMap: [], isFullDocument: false };
  }

  return Word.run(async (context) => {
    // Get selection paragraphs
    const selection = context.document.getSelection();
    const selectionParagraphs = selection.paragraphs;

    // Get body paragraphs for index mapping
    const bodyParagraphs = context.document.body.paragraphs;

    selectionParagraphs.load("items");
    bodyParagraphs.load("items");
    await context.sync();

    // Load text for all paragraphs
    for (const p of selectionParagraphs.items) {
      p.load("text");
    }
    for (const p of bodyParagraphs.items) {
      p.load("text");
    }
    await context.sync();

    // Build index map: map selection paragraph → body paragraph index
    // Use ordered scan to reduce ambiguity when paragraphs repeat
    const usedBodyIndices = new Set<number>();
    const indexMap: number[] = [];
    let lastFound = -1;

    for (const selPara of selectionParagraphs.items) {
      let foundIndex = -1;

      // 1) Prefer the next match after the last found index (preserves order)
      for (let i = lastFound + 1; i < bodyParagraphs.items.length; i++) {
        if (usedBodyIndices.has(i)) continue;
        if (bodyParagraphs.items[i].text === selPara.text) {
          foundIndex = i;
          break;
        }
      }

      // 2) Fallback: search any remaining match
      if (foundIndex === -1) {
        for (let i = 0; i < bodyParagraphs.items.length; i++) {
          if (usedBodyIndices.has(i)) continue;
          if (bodyParagraphs.items[i].text === selPara.text) {
            foundIndex = i;
            break;
          }
        }
      }

      if (foundIndex !== -1) {
        usedBodyIndices.add(foundIndex);
        lastFound = foundIndex;
      }

      indexMap.push(foundIndex); // -1 means not found; handle downstream
    }

    // Build indexed text for AI
    const text = selectionParagraphs.items
      .map((p, i) => `[${i}] ${p.text}`)
      .join("\n");

    console.log(`[getParagraphsIndexed] Selection has ${selectionParagraphs.items.length} paragraphs, indexMap:`, indexMap);

    return { text, indexMap, isFullDocument: false };
  });
}

/**
 * Legacy version for backwards compatibility - returns just the text
 * @deprecated Use getParagraphsIndexed() which returns ParagraphAnalysisData
 */
export async function getSelectionParagraphsText(): Promise<string> {
  const result = await getParagraphsIndexed();
  return result.text;
}

/**
 * Get ALL document paragraphs with indices (for full document analysis)
 * Returns paragraphs from the body - indexMap is identity (selection index = body index)
 */
export async function getBodyParagraphsIndexed(): Promise<ParagraphAnalysisData> {
  if (!isWordAvailable()) {
    return { text: "", indexMap: [], isFullDocument: true };
  }

  return Word.run(async (context) => {
    // Get paragraphs from BODY (entire document)
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items/text");
    await context.sync();

    const text = paragraphs.items
      .map((p, i) => `[${i}] ${p.text}`)
      .join("\n");

    // For full document, indexMap is identity: [0, 1, 2, 3, ...]
    const indexMap = paragraphs.items.map((_, i) => i);

    return { text, indexMap, isFullDocument: true };
  });
}

/**
 * Get paragraph indices mapping from selection to document
 * Returns the actual document paragraph indices for the selection
 */
export async function getSelectionParagraphIndices(): Promise<number[]> {
  if (!isWordAvailable()) {
    return [];
  }

  return Word.run(async (context) => {
    const bodyParagraphs = context.document.body.paragraphs;
    const selection = context.document.getSelection();
    const selectionParagraphs = selection.paragraphs;

    bodyParagraphs.load("items");
    selectionParagraphs.load("items");
    await context.sync();

    // Find the indices of selection paragraphs in the body
    const indices: number[] = [];
    for (const selPara of selectionParagraphs.items) {
      selPara.load("text");
    }
    for (const bodyPara of bodyParagraphs.items) {
      bodyPara.load("text");
    }
    await context.sync();

    for (const selPara of selectionParagraphs.items) {
      for (let i = 0; i < bodyParagraphs.items.length; i++) {
        if (bodyParagraphs.items[i].text === selPara.text) {
          if (!indices.includes(i)) {
            indices.push(i);
            break;
          }
        }
      }
    }

    return indices;
  });
}

// Word API search limit
const MAX_SEARCH_LENGTH = 255;

/**
 * Batch validation result
 */
export interface BatchValidationResult {
  index: number;
  valid: boolean;
  error?: string;
  foundCount?: number;
}

/**
 * Validate multiple corrections in a SINGLE Word.run call
 * Much faster than calling validateCorrection() for each one
 */
export async function validateCorrectionsBatch(
  corrections: Array<{ paragraph: number; original: string; occurrence: number }>
): Promise<BatchValidationResult[]> {
  if (!isWordAvailable()) {
    return corrections.map((_, i) => ({ index: i, valid: false, error: "Word API not available" }));
  }

  return Word.run(async (ctx) => {
    // Get paragraphs from SELECTION
    const selection = ctx.document.getSelection();
    const paragraphs = selection.paragraphs;
    paragraphs.load("items/text");
    await ctx.sync();

    const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const buildWhitespaceRegex = (value: string) => {
      const escaped = escapeRegex(value);
      return new RegExp(escaped.replace(/[\s\u00A0\u2007\u202F]+/g, "[\\s\\u00A0\\u2007\\u202F]+"));
    };
    const countWhitespaceMatches = (haystack: string, needle: string): number => {
      if (!needle || !needle.trim()) return 0;
      const regex = buildWhitespaceRegex(needle);
      const globalRegex = new RegExp(regex.source, "g");
      const matches = haystack.match(globalRegex);
      return matches ? matches.length : 0;
    };

    const results: BatchValidationResult[] = [];
    const totalParagraphs = paragraphs.items.length;

    // Store search results to reuse after sync
    const searchMap: Array<{
      idx: number;
      occurrence: number;
      original: string;
      paragraphText: string;
      searchResults: Word.RangeCollection;
    }> = [];

    // Queue all searches
    for (let i = 0; i < corrections.length; i++) {
      const c = corrections[i];

      // Check paragraph exists
      if (c.paragraph >= totalParagraphs) {
        results.push({ index: i, valid: false, error: "Parágrafo não encontrado" });
        continue;
      }

      // Check length limit
      if (c.original.length > MAX_SEARCH_LENGTH) {
        results.push({ index: i, valid: false, error: `Texto muito longo (máx ${MAX_SEARCH_LENGTH} chars)` });
        continue;
      }

      const paragraph = paragraphs.items[c.paragraph];
      const paragraphText = paragraph.text || "";
      const searchResults = paragraph.search(c.original, { matchCase: false });
      searchResults.load("items");

      searchMap.push({
        idx: i,
        occurrence: c.occurrence,
        original: c.original,
        paragraphText,
        searchResults
      });
    }

    // Sync all searches at once
    await ctx.sync();

    // Collect results from stored search objects
    for (const { idx, occurrence, original, paragraphText, searchResults } of searchMap) {
      let foundCount = searchResults.items.length;
      if (foundCount === 0) {
        // Fallback: whitespace-insensitive count (handles NBSP and multiple spaces)
        foundCount = countWhitespaceMatches(paragraphText, original);
      }
      if (foundCount === 0) {
        results.push({ index: idx, valid: false, error: "Texto não encontrado", foundCount: 0 });
      } else if (occurrence > foundCount) {
        results.push({
          index: idx,
          valid: false,
          error: `Só ${searchResults.items.length} ocorrência(s)`,
          foundCount
        });
      } else {
        results.push({ index: idx, valid: true, foundCount });
      }
    }

    // Sort by original index
    results.sort((a, b) => a.index - b.index);
    return results;
  });
}

/**
 * Validate a correction before applying
 * Uses SELECTION paragraphs (indices match what LLM received)
 */
export async function validateCorrection(
  paragraphIndex: number,
  original: string,
  occurrence: number
): Promise<ValidationResult> {
  if (!isWordAvailable()) {
    return { valid: false, error: "Word API not available" };
  }

  // Check search string length limit
  if (original.length > MAX_SEARCH_LENGTH) {
    return { valid: false, error: `Texto muito longo (máx ${MAX_SEARCH_LENGTH} chars)` };
  }

  return Word.run(async (ctx) => {
    // Use SELECTION paragraphs, not body
    const selection = ctx.document.getSelection();
    const paragraphs = selection.paragraphs;
    paragraphs.load("items");
    await ctx.sync();

    // 1. Parágrafo existe na seleção?
    if (paragraphIndex >= paragraphs.items.length) {
      return { valid: false, error: "Parágrafo não encontrado" };
    }

    const paragraph = paragraphs.items[paragraphIndex];

    // 2. Original existe no parágrafo?
    let results = paragraph.search(original, { matchCase: false });
    results.load("items");
    await ctx.sync();


    if (results.items.length === 0) {
      return { valid: false, error: "Texto não encontrado", foundCount: 0 };
    }

    // 3. Occurrence válida?
    if (occurrence > results.items.length) {
      return {
        valid: false,
        error: `Só ${results.items.length} ocorrência(s)`,
        foundCount: results.items.length
      };
    }

    return { valid: true, foundCount: results.items.length };
  });
}

/**
 * Apply a single surgical correction using occurrence
 * Returns: success status and optional error message
 * If bodyParagraphIndex is provided, uses body paragraphs (works after navigation)
 * Otherwise uses selection paragraphs (legacy behavior)
 */
export async function applySurgicalCorrection(
  paragraphIndex: number,
  original: string,
  occurrence: number,
  corrected: string,
  bodyParagraphIndex?: number
): Promise<{ success: boolean; error?: string }> {
  if (!isWordAvailable()) {
    return { success: false, error: "Word API not available" };
  }

  // Check search string length limit
  if (original.length > MAX_SEARCH_LENGTH) {
    return { success: false, error: `Texto muito longo (m??x ${MAX_SEARCH_LENGTH} chars)` };
  }

  return Word.run(async (ctx) => {
    // Use BODY paragraphs if bodyParagraphIndex is provided (works after navigation)
    // Otherwise fall back to selection paragraphs for backwards compatibility
    const useBodyIndex = bodyParagraphIndex !== undefined;
    const paragraphs = useBodyIndex
      ? ctx.document.body.paragraphs
      : ctx.document.getSelection().paragraphs;
    paragraphs.load("items/text");
    await ctx.sync();

    const targetIndex = useBodyIndex ? bodyParagraphIndex : paragraphIndex;

    // Valida????o 1: Par??grafo existe?
    if (targetIndex >= paragraphs.items.length) {
      console.warn(`[Correction] Paragraph ${targetIndex} not found`);
      return { success: false, error: "Par??grafo n??o encontrado" };
    }

    const paragraph = paragraphs.items[targetIndex];

    // Valida????o 2: Buscar original no par??grafo
    const results = paragraph.search(original, { matchCase: false });
    results.load("items");
    await ctx.sync();

    if (results.items.length === 0) {
      console.warn(`[Correction] Original "${original}" not found in paragraph ${targetIndex}`);
      return { success: false, error: "Texto n??o encontrado" };
    }

    // Valida????o 3: Occurrence v??lida?
    if (occurrence > results.items.length) {
      console.warn(`[Correction] Occurrence ${occurrence} invalid, only ${results.items.length} found`);
      return { success: false, error: `S?? ${results.items.length} ocorr??ncia(s)` };
    }

    // Aplica na occurrence correta (preserva formata????o!)
    results.items[occurrence - 1].insertText(corrected, Word.InsertLocation.replace);
    await ctx.sync();

    console.log(`[Correction] Applied: "${original}" ??? "${corrected}" (p${targetIndex}, occ${occurrence})`);
    return { success: true };
  });
}

export async function applyCorrections(
  corrections: CorrectionItem[]
): Promise<{ applied: number; failed: Array<{ original: string; error: string }> }> {
  const failed: Array<{ original: string; error: string }> = [];
  let applied = 0;

  for (const c of corrections) {
    try {
      const result = await applySurgicalCorrection(
        c.paragraph,
        c.original,
        c.occurrence,
        c.corrected,
        c.bodyParagraphIndex // Pass body index if available
      );
      if (result.success) {
        applied++;
      } else {
        failed.push({ original: c.original, error: result.error || "Erro desconhecido" });
      }
    } catch (error) {
      failed.push({ original: c.original, error: error instanceof Error ? error.message : "Erro desconhecido" });
    }
  }

  return { applied, failed };
}

/**
 * Navigate to and select a specific correction in the document
 * Uses BODY paragraph index (not selection-relative) to avoid issues
 * when the selection changes after previous navigations
 */
export async function navigateToCorrection(
  bodyParagraphIndex: number,
  original: string,
  occurrence: number
): Promise<{ success: boolean; error?: string }> {
  if (!isWordAvailable()) {
    return { success: false, error: "Word API not available" };
  }

  if (original.length > MAX_SEARCH_LENGTH) {
    return { success: false, error: "Texto muito longo" };
  }

  return Word.run(async (ctx) => {
    // Use BODY paragraphs (not selection) - this works even after selection changes
    const paragraphs = ctx.document.body.paragraphs;
    paragraphs.load("items/text");
    await ctx.sync();

    if (bodyParagraphIndex >= paragraphs.items.length) {
      return { success: false, error: "Parágrafo não encontrado" };
    }

    const paragraph = paragraphs.items[bodyParagraphIndex];
    const results = paragraph.search(original, { matchCase: false });
    results.load("items");
    await ctx.sync();

    if (results.items.length === 0) {
      return { success: false, error: "Texto não encontrado" };
    }

    if (occurrence > results.items.length) {
      return { success: false, error: "Ocorrência não encontrada" };
    }

    // Select the specific occurrence
    const targetRange = results.items[occurrence - 1];
    targetRange.select();
    await ctx.sync();

    console.log(`[Navigate] Selected: "${original}" (body p${bodyParagraphIndex}, occ${occurrence})`);
    return { success: true };
  });
}

/**
 * Enable or disable Track Changes
 */
export async function setTrackChanges(enabled: boolean): Promise<void> {
  if (!isWordAvailable()) {
    throw new Error("Word API not available");
  }

  return Word.run(async (context) => {
    context.document.changeTrackingMode = enabled
      ? Word.ChangeTrackingMode.trackAll
      : Word.ChangeTrackingMode.off;
    await context.sync();
    console.log(`[TrackChanges] ${enabled ? "Enabled" : "Disabled"}`);
  });
}

// ============================================================
// Search and Replace (for chat quick actions)
// ============================================================

/**
 * Search for text in the document and replace an occurrence
 * Uses case-sensitive search first (for exact AI matches), falls back to case-insensitive
 * Returns success status, error message, and count of total occurrences found
 */
export async function searchAndReplace(
  searchText: string,
  replaceText: string,
  occurrence: number = 1 // Which occurrence to replace (1-based)
): Promise<{ success: boolean; error?: string; totalFound?: number }> {
  if (!isWordAvailable()) {
    return { success: false, error: "Word API not available" };
  }

  // Validate search text
  if (!searchText || searchText.trim().length === 0) {
    return { success: false, error: "Texto de busca vazio" };
  }

  // Don't trim - preserve exact whitespace from AI
  const searchStr = searchText;

  // Check length limit
  if (searchStr.length > MAX_SEARCH_LENGTH) {
    return { success: false, error: `Texto muito longo (máx ${MAX_SEARCH_LENGTH} caracteres)` };
  }

  return Word.run(async (ctx) => {
    const body = ctx.document.body;

    // Try case-sensitive first (exact match from AI)
    let searchResults = body.search(searchStr, {
      matchCase: true,
      matchWholeWord: false,
    });
    searchResults.load("items");
    await ctx.sync();

    // If no exact match, try case-insensitive as fallback
    if (searchResults.items.length === 0) {
      console.log(`[SearchReplace] No exact match, trying case-insensitive`);
      searchResults = body.search(searchStr, {
        matchCase: false,
        matchWholeWord: false,
      });
      searchResults.load("items");
      await ctx.sync();
    }

    const totalFound = searchResults.items.length;

    if (totalFound === 0) {
      // Try with trimmed text as last resort
      const trimmedSearch = searchStr.trim();
      if (trimmedSearch !== searchStr) {
        searchResults = body.search(trimmedSearch, {
          matchCase: false,
          matchWholeWord: false,
        });
        searchResults.load("items");
        await ctx.sync();

        if (searchResults.items.length > 0) {
          console.log(`[SearchReplace] Found with trimmed search`);
          const targetIndex = Math.min(occurrence - 1, searchResults.items.length - 1);
          const targetResult = searchResults.items[targetIndex];
          targetResult.insertText(replaceText, Word.InsertLocation.replace);
          await ctx.sync();
          return { success: true, totalFound: searchResults.items.length };
        }
      }

      console.log(`[SearchReplace] Not found: "${searchStr.substring(0, 50)}..."`);
      return { success: false, error: "Texto não encontrado no documento", totalFound: 0 };
    }

    // Log warning if multiple occurrences
    if (totalFound > 1) {
      console.warn(`[SearchReplace] Multiple occurrences (${totalFound}), replacing #${occurrence}`);
    }

    // Replace the specified occurrence (1-based index)
    const targetIndex = Math.min(Math.max(0, occurrence - 1), totalFound - 1);
    const targetResult = searchResults.items[targetIndex];
    targetResult.insertText(replaceText, Word.InsertLocation.replace);
    await ctx.sync();

    console.log(`[SearchReplace] Replaced occurrence ${occurrence}/${totalFound}: "${searchStr.substring(0, 30)}..." → "${replaceText.substring(0, 30)}..."`);
    return { success: true, totalFound };
  });
}

// ============================================================
// Citation Navigation (for chat clickable references)
// ============================================================

/**
 * Search for text in the document and select an occurrence
 * Used for clickable citations in chat [[text]] and navigation between corrections
 * Tries case-sensitive first (exact match), then case-insensitive fallback
 * Returns success status, error message, and total occurrences found
 */
export async function searchAndSelectText(
  searchText: string,
  occurrence: number = 1 // Which occurrence to select (1-based)
): Promise<{ success: boolean; error?: string; totalFound?: number }> {
  if (!isWordAvailable()) {
    return { success: false, error: "Word API not available" };
  }

  // Validate search text
  if (!searchText || searchText.trim().length === 0) {
    return { success: false, error: "Texto de busca vazio" };
  }

  // Don't trim - preserve exact whitespace
  const searchStr = searchText;

  // Check length limit
  if (searchStr.length > MAX_SEARCH_LENGTH) {
    return { success: false, error: `Texto muito longo (máx ${MAX_SEARCH_LENGTH} caracteres)` };
  }

  return Word.run(async (ctx) => {
    const body = ctx.document.body;

    // Try case-sensitive first (exact match)
    let searchResults = body.search(searchStr, {
      matchCase: true,
      matchWholeWord: false,
    });
    searchResults.load("items");
    await ctx.sync();

    // Fallback to case-insensitive
    if (searchResults.items.length === 0) {
      searchResults = body.search(searchStr, {
        matchCase: false,
        matchWholeWord: false,
      });
      searchResults.load("items");
      await ctx.sync();
    }

    // Last resort: try with trimmed text
    if (searchResults.items.length === 0) {
      const trimmedText = searchStr.trim();
      if (trimmedText !== searchStr && trimmedText.length > 0) {
        searchResults = body.search(trimmedText, {
          matchCase: false,
          matchWholeWord: false,
        });
        searchResults.load("items");
        await ctx.sync();
      }
    }

    const totalFound = searchResults.items.length;

    if (totalFound === 0) {
      console.log(`[SearchSelect] Not found: "${searchStr.substring(0, 50)}..."`);
      return { success: false, error: "Texto não encontrado no documento", totalFound: 0 };
    }

    // Select the specified occurrence
    const targetIndex = Math.min(Math.max(0, occurrence - 1), totalFound - 1);
    const targetResult = searchResults.items[targetIndex];
    targetResult.select();
    await ctx.sync();

    console.log(`[SearchSelect] Selected occurrence ${occurrence}/${totalFound}: "${searchStr.substring(0, 30)}..."`);
    return { success: true, totalFound };
  });
}

// ============================================================
// Paragraph Navigation (for indexed analysis)
// ============================================================

/**
 * Navigate to a specific paragraph and optionally select text within it
 * Uses paragraph index from the analysis results
 * Returns success status and error message if any
 */
export async function navigateToParagraphText(
  paragraphIndex: number,
  searchText?: string,
  occurrence: number = 1
): Promise<{ success: boolean; error?: string }> {
  if (!isWordAvailable()) {
    return { success: false, error: "Word API not available" };
  }

  return Word.run(async (ctx) => {
    const paragraphs = ctx.document.body.paragraphs;
    paragraphs.load("items/text");
    await ctx.sync();

    // Check if paragraph index is valid
    if (paragraphIndex < 0 || paragraphIndex >= paragraphs.items.length) {
      console.warn(`[NavParagraph] Invalid index ${paragraphIndex}, total paragraphs: ${paragraphs.items.length}`);
      return { success: false, error: `Parágrafo [${paragraphIndex}] não encontrado` };
    }

    const targetParagraph = paragraphs.items[paragraphIndex];
    const paragraphText = targetParagraph.text || "";

    const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const buildWhitespaceRegex = (value: string) => {
      const escaped = escapeRegex(value);
      return new RegExp(escaped.replace(/[\s\u00A0\u2007\u202F]+/g, "[\\s\\u00A0\\u2007\\u202F]+"));
    };
    const findWhitespaceMatches = (haystack: string, needle: string): string[] => {
      if (!needle || !needle.trim()) return [];
      const regex = buildWhitespaceRegex(needle);
      const matches: string[] = [];
      const globalRegex = new RegExp(regex.source, "g");
      let match: RegExpExecArray | null;
      while ((match = globalRegex.exec(haystack)) !== null) {
        matches.push(match[0]);
      }
      return matches;
    };

    // If no search text, just select the entire paragraph
    if (!searchText || !searchText.trim()) {
      targetParagraph.select();
      await ctx.sync();
      console.log(`[NavParagraph] Selected paragraph ${paragraphIndex}`);
      return { success: true };
    }

    // If search text is too long, fall back to selecting the paragraph
    let searchStr = searchText;
    if (searchStr.length > MAX_SEARCH_LENGTH) {
      console.warn(`[NavParagraph] Search text too long (${searchStr.length} chars), selecting paragraph instead`);
      targetParagraph.select();
      await ctx.sync();
      return { success: true };
    }

    // Search within this specific paragraph
    const searchResults = targetParagraph.search(searchStr, {
      matchCase: true,
      matchWholeWord: false,
    });
    searchResults.load("items");
    await ctx.sync();

    // Try case-insensitive if no exact match
    if (searchResults.items.length === 0) {
      const searchResultsCI = targetParagraph.search(searchStr, {
        matchCase: false,
        matchWholeWord: false,
      });
      searchResultsCI.load("items");
      await ctx.sync();

      if (searchResultsCI.items.length > 0) {
        // Validate occurrence is within range (strict - don't clamp)
        if (occurrence > searchResultsCI.items.length) {
          console.warn(`[NavParagraph] Occurrence ${occurrence} out of range (found ${searchResultsCI.items.length})`);
          return { success: false, error: `Ocorrência ${occurrence} não existe (encontradas: ${searchResultsCI.items.length})` };
        }
        const targetIndex = occurrence - 1;
        searchResultsCI.items[targetIndex].select();
        await ctx.sync();
        console.log(`[NavParagraph] Found text (case-insensitive) in paragraph ${paragraphIndex}`);
        return { success: true };
      }

      // Fall back to selecting the whole paragraph
      targetParagraph.select();
      await ctx.sync();
      console.log(`[NavParagraph] Text not found in paragraph ${paragraphIndex}, selected paragraph`);
      return { success: true };
    }

    // Validate occurrence is within range (strict - don't clamp)
    if (occurrence > searchResults.items.length) {
      console.warn(`[NavParagraph] Occurrence ${occurrence} out of range (found ${searchResults.items.length})`);
      return { success: false, error: `Ocorrência ${occurrence} não existe (encontradas: ${searchResults.items.length})` };
    }

    // Select the specified occurrence
    const targetIndex = occurrence - 1;
    searchResults.items[targetIndex].select();
    await ctx.sync();
    console.log(`[NavParagraph] Selected text in paragraph ${paragraphIndex}, occurrence ${occurrence}`);
    return { success: true };
  });
}

/**
 * Replace text within a specific paragraph
 * More precise than document-wide search
 */
export async function replaceInParagraph(
  paragraphIndex: number,
  searchText: string,
  replaceText: string,
  occurrence: number = 1
): Promise<{ success: boolean; error?: string }> {
  if (!isWordAvailable()) {
    return { success: false, error: "Word API not available" };
  }

  if (!searchText || !searchText.trim()) {
    return { success: false, error: "Texto de busca vazio" };
  }

  // Check search string length limit (Word API limit is 255 chars)
  if (searchText.length > MAX_SEARCH_LENGTH) {
    return { success: false, error: `Texto muito longo (máx ${MAX_SEARCH_LENGTH} caracteres)` };
  }

  return Word.run(async (ctx) => {
    const paragraphs = ctx.document.body.paragraphs;
    paragraphs.load("items/text");
    await ctx.sync();

    // Check if paragraph index is valid
    if (paragraphIndex < 0 || paragraphIndex >= paragraphs.items.length) {
      console.warn(`[ReplaceParagraph] Invalid index ${paragraphIndex}`);
      return { success: false, error: `Parágrafo [${paragraphIndex}] não encontrado` };
    }

    const targetParagraph = paragraphs.items[paragraphIndex];
    const paragraphText = targetParagraph.text || "";

    const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const buildWhitespaceRegex = (value: string) => {
      const escaped = escapeRegex(value);
      return new RegExp(escaped.replace(/[\s\u00A0\u2007\u202F]+/g, "[\\s\\u00A0\\u2007\\u202F]+"));
    };
    const findWhitespaceMatches = (haystack: string, needle: string): string[] => {
      if (!needle || !needle.trim()) return [];
      const regex = buildWhitespaceRegex(needle);
      const matches: string[] = [];
      const globalRegex = new RegExp(regex.source, "g");
      let match: RegExpExecArray | null;
      while ((match = globalRegex.exec(haystack)) !== null) {
        matches.push(match[0]);
      }
      return matches;
    };

    // Search within this specific paragraph (case-sensitive first)
    let searchResults = targetParagraph.search(searchText, {
      matchCase: true,
      matchWholeWord: false,
    });
    searchResults.load("items");
    await ctx.sync();

    // Try case-insensitive if no exact match
    if (searchResults.items.length === 0) {
      searchResults = targetParagraph.search(searchText, {
        matchCase: false,
        matchWholeWord: false,
      });
      searchResults.load("items");
      await ctx.sync();
    }

    let targetOccurrence = occurrence;

    if (searchResults.items.length === 0) {
      // Fallback: whitespace-insensitive match (handles NBSP and multiple spaces)
      const matches = findWhitespaceMatches(paragraphText, searchText);
      if (matches.length > 0) {
        if (targetOccurrence > matches.length) {
          console.warn(`[ReplaceParagraph] Occurrence ${occurrence} out of range (whitespace-insensitive found ${matches.length})`);
          return { success: false, error: `Ocorrência ${targetOccurrence} não existe (encontradas: ${matches.length})` };
        }
        const matchText = matches[targetOccurrence - 1];
        targetOccurrence = 1; // Occurrence already resolved by match selection
        searchResults = targetParagraph.search(matchText, {
          matchCase: true,
          matchWholeWord: false,
        });
        searchResults.load("items");
        await ctx.sync();
      }
    }

    if (searchResults.items.length === 0) {
      console.log(`[ReplaceParagraph] Text not found in paragraph ${paragraphIndex}: "${searchText.substring(0, 30)}..."`);
      return { success: false, error: "Texto não encontrado no parágrafo" };
    }

    // Validate occurrence is within range (strict - don't clamp to avoid wrong replacements)
    if (targetOccurrence > searchResults.items.length) {
      console.warn(`[ReplaceParagraph] Occurrence ${occurrence} out of range (found ${searchResults.items.length})`);
      return { success: false, error: `Ocorrência ${targetOccurrence} não existe (encontradas: ${searchResults.items.length})` };
    }

    // Replace the specified occurrence
    const targetIndex = targetOccurrence - 1;
    searchResults.items[targetIndex].insertText(replaceText, Word.InsertLocation.replace);
    await ctx.sync();

    console.log(`[ReplaceParagraph] Replaced in paragraph ${paragraphIndex}: "${searchText.substring(0, 20)}..." → "${replaceText.substring(0, 20)}..."`);
    return { success: true };
  });
}

/**
 * Replace text using context-based anchor approach.
 * This is more precise than occurrence-based replacement for short targets.
 *
 * Algorithm:
 * 1. Search for the context string (must be unique in paragraph)
 * 2. Within that context range, search for the target (must be unique in context)
 * 3. Replace only the target within the found range
 *
 * @param paragraphIndex Body paragraph index (0-based)
 * @param contexto Anchor text that contains the target (must be unique in paragraph)
 * @param target Text to replace within the context
 * @param replacement Text to replace target with
 */
export async function replaceWithContext(
  paragraphIndex: number,
  contexto: string,
  target: string,
  replacement: string
): Promise<{ success: boolean; error?: string }> {
  // Validate inputs
  if (!contexto || !target) {
    return { success: false, error: "Contexto e target são obrigatórios" };
  }

  if (!contexto.includes(target)) {
    return { success: false, error: `Target "${target}" não encontrado no contexto "${contexto}"` };
  }

  if (contexto.length > MAX_SEARCH_LENGTH) {
    return { success: false, error: `Contexto muito longo (máx ${MAX_SEARCH_LENGTH} caracteres)` };
  }

  return Word.run(async (ctx) => {
    const paragraphs = ctx.document.body.paragraphs;
    paragraphs.load("items/text");
    await ctx.sync();

    // Check if paragraph index is valid
    if (paragraphIndex < 0 || paragraphIndex >= paragraphs.items.length) {
      console.warn(`[ReplaceWithContext] Invalid index ${paragraphIndex}`);
      return { success: false, error: `Parágrafo [${paragraphIndex}] não encontrado` };
    }

    const targetParagraph = paragraphs.items[paragraphIndex];

    // Helper: swap degree (°) and ordinal (º) symbols for fallback search
    // These are visually identical but different Unicode characters (U+00B0 vs U+00BA)
    const swapOrdinalSymbols = (text: string): string => {
      if (text.includes("°")) return text.replace(/°/g, "º");
      if (text.includes("º")) return text.replace(/º/g, "°");
      return text;
    };

    // Step 1: Search for the context (anchor) - must be unique
    let searchContexto = contexto;
    let contextResults = targetParagraph.search(searchContexto, {
      matchCase: true,
      matchWholeWord: false,
    });
    contextResults.load("items");
    await ctx.sync();

    // Try case-insensitive if no exact match
    if (contextResults.items.length === 0) {
      contextResults = targetParagraph.search(searchContexto, {
        matchCase: false,
        matchWholeWord: false,
      });
      contextResults.load("items");
      await ctx.sync();
    }

    // Fallback: try with swapped ordinal symbols (° ↔ º)
    if (contextResults.items.length === 0) {
      const swappedContexto = swapOrdinalSymbols(contexto);
      if (swappedContexto !== contexto) {
        console.log(`[ReplaceWithContext] Trying with swapped ordinal symbols: "${swappedContexto.substring(0, 30)}..."`);
        searchContexto = swappedContexto;
        contextResults = targetParagraph.search(searchContexto, {
          matchCase: false,
          matchWholeWord: false,
        });
        contextResults.load("items");
        await ctx.sync();
      }
    }

    if (contextResults.items.length === 0) {
      console.log(`[ReplaceWithContext] Context not found: "${contexto.substring(0, 40)}..."`);
      return { success: false, error: `Contexto não encontrado: "${contexto.substring(0, 30)}..."` };
    }

    if (contextResults.items.length > 1) {
      console.warn(`[ReplaceWithContext] Context appears ${contextResults.items.length}x (must be unique)`);
      return { success: false, error: `Contexto aparece ${contextResults.items.length}x no parágrafo (deve ser único)` };
    }

    const contextRange = contextResults.items[0];

    // Step 2: Within the context range, search for the target - must be unique in context
    // For short ALPHANUMERIC targets (≤2 chars like "a" for crase), use matchWholeWord to avoid
    // matching letters inside words (e.g., "a" in "visando" vs standalone "a")
    // Don't use for punctuation/whitespace targets like " ," (matchWholeWord doesn't apply)
    const isAlphanumericTarget = /^[\p{L}\p{N}]+$/u.test(target);
    const useWholeWord = target.length <= 2 && isAlphanumericTarget;
    let targetResults = contextRange.search(target, {
      matchCase: true,
      matchWholeWord: useWholeWord,
    });
    targetResults.load("items");
    await ctx.sync();

    // Try case-insensitive if no exact match
    if (targetResults.items.length === 0) {
      targetResults = contextRange.search(target, {
        matchCase: false,
        matchWholeWord: useWholeWord,
      });
      targetResults.load("items");
      await ctx.sync();
    }

    // Fallback for short targets: try without matchWholeWord if not found
    // (handles edge cases where context contains the target attached to punctuation)
    if (targetResults.items.length === 0 && useWholeWord) {
      targetResults = contextRange.search(target, {
        matchCase: false,
        matchWholeWord: false,
      });
      targetResults.load("items");
      await ctx.sync();
    }

    // Fallback: try with swapped ordinal symbols (° ↔ º) for target
    if (targetResults.items.length === 0) {
      const swappedTarget = swapOrdinalSymbols(target);
      if (swappedTarget !== target) {
        console.log(`[ReplaceWithContext] Trying target with swapped ordinal: "${swappedTarget}"`);
        targetResults = contextRange.search(swappedTarget, {
          matchCase: false,
          matchWholeWord: false,
        });
        targetResults.load("items");
        await ctx.sync();
      }
    }

    if (targetResults.items.length === 0) {
      console.log(`[ReplaceWithContext] Target not found in context: "${target}"`);
      return { success: false, error: `Target "${target}" não encontrado no contexto` };
    }

    if (targetResults.items.length > 1) {
      console.warn(`[ReplaceWithContext] Target appears ${targetResults.items.length}x in context (must be unique)`);
      return { success: false, error: `Target "${target}" aparece ${targetResults.items.length}x no contexto (deve ser único)` };
    }

    // Step 3: Replace the target
    targetResults.items[0].insertText(replacement, Word.InsertLocation.replace);
    await ctx.sync();

    console.log(`[ReplaceWithContext] Replaced in paragraph ${paragraphIndex}: "${target}" → "${replacement}" (context: "${contexto.substring(0, 20)}...")`);
    return { success: true };
  });
}

// ============================================================
// Trailing Whitespace Cleanup
// ============================================================

/**
 * Result of whitespace cleanup operation
 */
export interface WhitespaceCleanupResult {
  fixed: number; // Number of issues fixed
  details: string[]; // Description of fixes for optional user notification
}

/**
 * Normalize all types of whitespace to regular space.
 * Word documents often contain NBSP (U+00A0) and other Unicode whitespace
 * that looks like a space but won't be found by regular space search.
 *
 * Common whitespace characters:
 * - U+0020: Regular space
 * - U+00A0: No-Break Space (NBSP) - VERY common in Word
 * - U+2002: En Space
 * - U+2003: Em Space
 * - U+2007: Figure Space
 * - U+202F: Narrow No-Break Space
 */
function normalizeWhitespace(text: string): string {
  // Replace all Unicode whitespace variants with regular space
  // This regex matches various Unicode space characters
  return text.replace(/[\u00A0\u2002\u2003\u2007\u202F]/g, " ");
}

/**
 * Fix whitespace issues in text:
 * 1. Multiple spaces → single space
 * 2. Space before punctuation → just punctuation
 * 3. Trailing spaces → remove
 *
 * Returns the fixed text and count of changes.
 */
function fixWhitespaceIssues(text: string): { fixed: string; changeCount: number; changes: string[] } {
  const changes: string[] = [];
  let changeCount = 0;
  let result = text;

  // 1. First normalize all whitespace to regular spaces
  const normalized = normalizeWhitespace(result);
  if (normalized !== result) {
    result = normalized;
    changeCount++;
    changes.push("normalized whitespace");
  }

  // 2. Fix multiple consecutive spaces → single space
  const multiSpacePattern = /  +/g;
  const multiSpaceMatches = result.match(multiSpacePattern);
  if (multiSpaceMatches) {
    result = result.replace(multiSpacePattern, " ");
    changeCount += multiSpaceMatches.length;
    changes.push(`${multiSpaceMatches.length}x double spaces`);
  }

  // 3. Fix space before final punctuation: "CPC ." → "CPC."
  // Also handles: "artigo ," "seção :" etc.
  const spaceBeforePunctPattern = / +([.,:;!?])( *)$/;
  const punctMatch = spaceBeforePunctPattern.exec(result);
  if (punctMatch) {
    result = result.replace(spaceBeforePunctPattern, "$1");
    changeCount++;
    changes.push(`space before "${punctMatch[1]}"`);
  }

  // 4. Remove trailing spaces (that weren't already handled by step 3)
  const trailingSpacePattern = / +$/;
  if (trailingSpacePattern.test(result)) {
    result = result.replace(trailingSpacePattern, "");
    changeCount++;
    changes.push("trailing spaces");
  }

  return { fixed: result, changeCount, changes };
}

/**
 * Clean up whitespace issues in paragraphs BEFORE sending to AI.
 * This saves tokens and ensures reliable fixes for common issues:
 * - Double spaces in the middle of text (e.g., "de  reunião" → "de reunião")
 * - Trailing spaces at end of paragraphs
 * - Space before final punctuation (e.g., "CPC ." → "CPC.")
 *
 * IMPORTANT: This function handles NBSP and other Unicode whitespace that
 * Word's search API cannot find when searching for regular spaces.
 *
 * @param scope - 'selection' for selected text, 'document' for entire document
 * @returns Number of fixes made and details
 */
export async function cleanupTrailingWhitespace(
  scope: "selection" | "document" = "document"
): Promise<WhitespaceCleanupResult> {
  if (!isWordAvailable()) {
    return { fixed: 0, details: [] };
  }

  return Word.run(async (ctx) => {
    // Preserve and temporarily disable Track Changes to avoid creating revisions
    ctx.document.load("changeTrackingMode");
    await ctx.sync();
    const originalTrackingMode = ctx.document.changeTrackingMode;
    if (originalTrackingMode !== Word.ChangeTrackingMode.off) {
      ctx.document.changeTrackingMode = Word.ChangeTrackingMode.off;
      await ctx.sync();
    }

    const paragraphs =
      scope === "selection"
        ? ctx.document.getSelection().paragraphs
        : ctx.document.body.paragraphs;

    paragraphs.load("items");
    await ctx.sync();

    // Load text for all paragraphs
    for (const p of paragraphs.items) {
      p.load("text");
    }
    await ctx.sync();

    const details: string[] = [];
    let totalFixed = 0;

    for (let i = 0; i < paragraphs.items.length; i++) {
      const para = paragraphs.items[i];
      const originalText = para.text;

      // Skip empty paragraphs (Word often has trailing carriage returns)
      if (!originalText || originalText.trim().length === 0) {
        continue;
      }

      let changeCount = 0;
      const changes: string[] = [];

      // Helper: reload paragraph text (Word includes trailing CR)
      const loadText = async () => {
        para.load("text");
        await ctx.sync();
        const text = para.text || "";
        const hasCR = text.endsWith("\r");
        return {
          text,
          textNoCR: hasCR ? text.slice(0, -1) : text,
        };
      };

      // 1) Normalize NBSP and other Unicode spaces → regular space
      const unicodeSpaces = ["\u00A0", "\u2002", "\u2003", "\u2007", "\u202F"];
      let normalizedCount = 0;
      for (const ws of unicodeSpaces) {
        const results = para.search(ws, { matchCase: true });
        results.load("items");
        await ctx.sync();
        if (results.items.length > 0) {
          results.items.forEach((r) => r.insertText(" ", Word.InsertLocation.replace));
          normalizedCount += results.items.length;
          await ctx.sync();
        }
      }
      if (normalizedCount > 0) {
        changeCount += normalizedCount;
        changes.push(`normalized ${normalizedCount} space(s)`);
      }

      // 1b) Normalize degree symbol (°) to ordinal (º)
      // These are visually identical but different Unicode (U+00B0 vs U+00BA)
      // Standardizing prevents AI/document mismatch errors
      const degreeResults = para.search("°", { matchCase: true });
      degreeResults.load("items");
      await ctx.sync();
      if (degreeResults.items.length > 0) {
        degreeResults.items.forEach((r) => r.insertText("º", Word.InsertLocation.replace));
        changeCount += degreeResults.items.length;
        changes.push(`normalized ${degreeResults.items.length} degree symbol(s)`);
        await ctx.sync();
      }

      // 2) Remove double spaces (iterate to handle long runs)
      let { textNoCR } = await loadText();
      let doubleSpaceCount = 0;
      while (textNoCR.includes("  ")) {
        const results = para.search("  ", { matchCase: true });
        results.load("items");
        await ctx.sync();
        if (results.items.length === 0) break;
        results.items[0].insertText(" ", Word.InsertLocation.replace);
        await ctx.sync();
        doubleSpaceCount++;
        ({ textNoCR } = await loadText());
      }
      if (doubleSpaceCount > 0) {
        changeCount += doubleSpaceCount;
        changes.push(`removed ${doubleSpaceCount} double space(s)`);
      }

      // 2b) Normalize mixed dash sequences (e.g., "-—", "–—", "- —")
      // Dash characters:
      // - U+002D HYPHEN-MINUS (keyboard -)
      // – U+2013 EN DASH
      // — U+2014 EM DASH
      // ‒ U+2012 FIGURE DASH
      // ― U+2015 HORIZONTAL BAR
      const dashReplacements: Array<{ pattern: string; replacement: string; label: string }> = [
        // Adjacent mixed dashes (no space) → single em dash
        { pattern: "-—", replacement: "—", label: "\"-—\" → \"—\"" },
        { pattern: "–—", replacement: "—", label: "\"–—\" → \"—\"" },
        { pattern: "—-", replacement: "—", label: "\"—-\" → \"—\"" },
        { pattern: "—–", replacement: "—", label: "\"—–\" → \"—\"" },
        { pattern: "–-", replacement: "—", label: "\"–-\" → \"—\"" },
        { pattern: "-–", replacement: "—", label: "\"-–\" → \"—\"" },
        { pattern: "--", replacement: "—", label: "\"--\" → \"—\"" },
        // With spaces
        { pattern: "- —", replacement: " — ", label: "\"- —\" → \" — \"" },
        { pattern: "– —", replacement: " — ", label: "\"– —\" → \" — \"" },
        { pattern: "— -", replacement: " — ", label: "\"— -\" → \" — \"" },
        { pattern: "— –", replacement: " — ", label: "\"— –\" → \" — \"" },
        // Double em-dashes (unlikely but possible)
        { pattern: "——", replacement: "—", label: "\"——\" → \"—\"" },
        { pattern: "––", replacement: "—", label: "\"––\" → \"—\"" },
      ];
      let dashFixCount = 0;
      for (const { pattern, replacement, label } of dashReplacements) {
        // Process each paragraph repeatedly until no more matches (handles overlapping patterns)
        let foundAny = true;
        while (foundAny) {
          const results = para.search(pattern, { matchCase: true });
          results.load("items");
          await ctx.sync();
          if (results.items.length > 0) {
            // Replace one at a time to avoid issues with overlapping replacements
            results.items[0].insertText(replacement, Word.InsertLocation.replace);
            await ctx.sync();
            dashFixCount++;
            if (!changes.some(c => c.startsWith(label.split(" ")[0]))) {
              changes.push(label);
            }
          } else {
            foundAny = false;
          }
        }
      }
      if (dashFixCount > 0) {
        changeCount += dashFixCount;
      }

      // 3) Remove space before final punctuation (end-of-paragraph)
      ({ textNoCR } = await loadText());
      const spaceBeforePunctPattern = / +([.,:;!?]) *$/;
      const punctMatch = spaceBeforePunctPattern.exec(textNoCR);
      if (punctMatch) {
        const searchText = punctMatch[0].replace(/ +$/, ""); // " ." or " ,"
        const replaceText = punctMatch[1];
        const results = para.search(searchText, { matchCase: true });
        results.load("items");
        await ctx.sync();
        if (results.items.length > 0) {
          results.items[results.items.length - 1].insertText(replaceText, Word.InsertLocation.replace);
          await ctx.sync();
          changeCount++;
          changes.push(`space before "${replaceText}"`);
        }
      }

      // 4) Remove trailing spaces
      ({ textNoCR } = await loadText());
      const trailingMatch = textNoCR.match(/ +$/);
      if (trailingMatch) {
        const trailingSpaces = trailingMatch[0];
        const trimmed = textNoCR.slice(0, -trailingSpaces.length);
        if (trimmed.length > 0) {
          const lastChar = trimmed.slice(-1);
          const searchText = lastChar + trailingSpaces;
          const results = para.search(searchText, { matchCase: true });
          results.load("items");
          await ctx.sync();
          if (results.items.length > 0) {
            results.items[results.items.length - 1].insertText(lastChar, Word.InsertLocation.replace);
            await ctx.sync();
            changeCount++;
            changes.push(`removed ${trailingSpaces.length} trailing space(s)`);
          }
        }
      }

      if (changeCount > 0) {
        details.push(`P${i}: ${changes.join(", ")}`);
        totalFixed += changeCount;
      }
    }

    // Sync all changes at once
    if (totalFixed > 0) {
      await ctx.sync();
      console.log(`[WhitespaceCleanup] Fixed ${totalFixed} issues:`, details);
    }

    // Restore original Track Changes mode
    if (originalTrackingMode !== Word.ChangeTrackingMode.off) {
      ctx.document.changeTrackingMode = originalTrackingMode;
      await ctx.sync();
    }

    return { fixed: totalFixed, details };
  });
}
